import apiClient from './apiClient';
import { useAppStateStore } from '../stores/appState'
import { useUserStateStore } from '../stores/userState';
import { decodeBase64 } from '../utils/utils';

/**
 * API 요청 함수
 * @param {string} url - 요청 URL (baseURL 이후 경로)
 * @param {Object} [data] - 요청 데이터
 * @param {string} [method='POST'] - HTTP 메서드 (기본값: POST)
 * @returns {Promise} - API 호출 결과
 */
export function callApi(url, data, associationID, sAuditID = '-1', contentType = 'application/json', responseType = "json", method = 'POST') {
  let response = null;
  const appState = useAppStateStore();
  const userState = useUserStateStore();
  try {

    appState.openProgress();

    // 요청 설정 객체
    const config = {
      url,
      method,
      headers: { 'Content-Type': contentType },
      responseType
    };

    // 데이터가 FormData라면 브라우저가 스스로 헤더를 정하도록 Content-Type을 삭제
    if (data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    if (data !== null) {
      config.data = data;
    }

    // associationID가 있는 경우에만 config에 추가
    if (associationID !== null) {
      config.headers['AssociationID'] = associationID;
    }

    const rawActionId =
      userState.user.UserID === ''
        ? data?.UserID ?? 'NoLogin'
        : `${userState.user.UserID}(${userState.user.UserNameLc})`;

    config.headers['ActionID'] = decodeBase64(rawActionId);
    config.headers['SAuditID'] = sAuditID;
    response = apiClient(config);
  } catch (error) {
    console.error('API 호출 에러:', error);
    throw error;
  } finally {
    appState.closeProgress();
  }
  return response;
}

/**
 * Multipart/form-data 업로드 전용 API 호출 함수.
 *
 * 사용 예 (BoardCompose 의 첨부 파일 업로드):
 *   const fd = new FormData();
 *   fd.append('file', fileObject);
 *   fd.append('TempOwner', userState.user.UserID);
 *
 *   const res = await callApiMultipart(
 *     '/API/uploadTempBoardFile',
 *     fd,
 *     userState.AssociationID,
 *     {
 *       onProgress: (loaded, total, percent) => {
 *         entry.progress = percent;
 *       },
 *     },
 *   );
 *
 * @param {string} url                       요청 URL (baseURL 이후 경로)
 * @param {FormData} formData                업로드할 FormData (필수 — 아닐 시 throw)
 * @param {string|number|null} associationID 멀티테넌시 헤더 값
 * @param {Object} [options]                 추가 옵션
 * @param {(loaded:number,total:number,percent:number)=>void} [options.onProgress] 업로드 진행률 콜백
 * @param {string|number} [options.sAuditID='-1']
 * @param {number} [options.timeout=120000]  업로드용 타임아웃 (기본 2분, 큰 파일 대비)
 * @param {string} [options.method='POST']
 * @returns {Promise} apiClient 응답 (response.data 가 그대로 반환됨 — apiClient 인터셉터)
 */
export function callApiMultipart(url, formData, associationID, options = {}) {
  const {
    onProgress = null,
    sAuditID = '-1',
    timeout = 120000,
    method = 'POST',
  } = options;

  const appState = useAppStateStore();
  const userState = useUserStateStore();
  let response = null;

  try {
    appState.openProgress();

    if (!(formData instanceof FormData)) {
      throw new TypeError('callApiMultipart: 두 번째 인자는 FormData 인스턴스여야 합니다.');
    }

    // 요청 설정 — Content-Type 은 axios + 브라우저가 boundary 와 함께 자동 설정하므로 명시하지 않음
    const config = {
      url,
      method,
      data: formData,
      headers: {},
      responseType: 'json',
      timeout,
      // axios 가 업로드 progress 마다 호출
      onUploadProgress: (e) => {
        if (typeof onProgress !== 'function') return;
        const total = e.total ?? 0;
        const loaded = e.loaded ?? 0;
        const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
        try {
          onProgress(loaded, total, percent);
        } catch (cbErr) {
          // 호출자 콜백에서 예외가 나도 업로드 자체는 계속 진행
          console.error('callApiMultipart onProgress 콜백 에러:', cbErr);
        }
      },
    };

    if (associationID !== null && associationID !== undefined) {
      config.headers['AssociationID'] = associationID;
    }

    const rawActionId =
      userState.user.UserID === ''
        ? (formData.get('UserID') ?? 'NoLogin')
        : `${userState.user.UserID}(${userState.user.UserNameLc})`;

    config.headers['ActionID'] = decodeBase64(rawActionId);
    config.headers['SAuditID'] = sAuditID;

    response = apiClient(config);
  } catch (error) {
    console.error('API 호출 에러 (multipart):', error);
    throw error;
  } finally {
    appState.closeProgress();
  }

  return response;
}
